import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { formatWhen } from './types';
import { currentSubscription, needsHomeScreen, PUSH_KEY, pushSupported, subscribeThisDevice, type PushSettings as Settings } from './push';

const REASONS = {
  unsupported: 'Ta przeglądarka nie obsługuje powiadomień.',
  denied: 'Powiadomienia są zablokowane dla tej strony. Zezwól na nie w ustawieniach przeglądarki (ikona kłódki przy adresie) i spróbuj ponownie.',
  failed: 'Nie udało się włączyć powiadomień na tym urządzeniu. Spróbuj ponownie za chwilę.',
} as const;

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

/**
 * "Powiadomienia" of "Moje konto": push on this device (permission + subscription), my devices, and which kinds of
 * bell notifications also go to push. On an iPhone outside the Home Screen only the hint how to add the app there.
 */
export function PushSettings() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: PUSH_KEY, queryFn: () => api<Settings>('/api/me/push') });
  const [thisEndpoint, setThisEndpoint] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [tested, setTested] = useState<string | null>(null);
  // a ticked kind shows at once; the server's answer follows (on an error it goes back)
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const refresh = () => queryClient.invalidateQueries({ queryKey: PUSH_KEY });

  useEffect(() => {
    void currentSubscription().then((s) => setThisEndpoint(s?.endpoint ?? null));
  }, [settings.data]);

  const enable = useMutation({
    mutationFn: async () => {
      setProblem(null);
      const result = await subscribeThisDevice(settings.data!.publicKey!);
      if (!result.ok) {
        setProblem(REASONS[result.reason]);
        return;
      }
      await api('/api/me/push/subscriptions', { method: 'POST', body: { endpoint: result.subscription.endpoint, keys: result.subscription.keys } });
      setThisEndpoint(result.subscription.endpoint ?? null);
    },
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: async (device: Settings['devices'][number]) => {
      await api(`/api/me/push/subscriptions/${device.id}`, { method: 'DELETE' });
      // this browser: also stop its subscription, so it does not wait for messages that will not come
      if (device.endpoint === thisEndpoint) await (await currentSubscription())?.unsubscribe();
    },
    onSettled: refresh,
  });
  const toggle = useMutation({
    mutationFn: (t: { id: string; enabled: boolean }) => api(`/api/me/push/types/${encodeURIComponent(t.id)}`, { method: 'PUT', body: { enabled: t.enabled } }),
    onMutate: (t) => setTicked((m) => ({ ...m, [t.id]: t.enabled })),
    onError: (_e, t) =>
      setTicked((m) => {
        const { [t.id]: _gone, ...rest } = m;
        return rest;
      }),
    onSettled: refresh,
  });
  const test = useMutation({
    mutationFn: () => api<{ status: string | null; error: string | null; devices: number }>('/api/me/push/test', { method: 'POST' }),
    onSuccess: (r) => setTested(r.status === 'sent' ? 'Wysłano. Powiadomienie powinno pojawić się za chwilę.' : r.error ? `Nie udało się: ${r.error}` : 'Nie masz jeszcze urządzeń z włączonymi powiadomieniami.'),
  });

  const data = settings.data;
  const supported = pushSupported();
  const homeScreen = needsHomeScreen();
  const onThisDevice = !!data && !!thisEndpoint && data.devices.some((d) => d.endpoint === thisEndpoint);
  const error = enable.error ?? remove.error ?? toggle.error ?? test.error;

  return (
    <section className="settings-section push-settings" data-feature-id="notifications.push" aria-labelledby="push-title">
      <h2 id="push-title">Powiadomienia</h2>
      <p className="hint">Powiadomienia z dzwonka mogą przychodzić też na telefon i komputer — także gdy aplikacja jest zamknięta.</p>
      {settings.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {settings.error && <p className="alert" role="alert">{errorText(settings.error, 'Nie udało się wczytać ustawień powiadomień')}</p>}
      {data && !data.enabled && <p className="muted">Powiadomienia na telefon i komputer nie są jeszcze włączone na serwerze (instrukcja wdrożenia, punkt 16).</p>}
      {data?.enabled && (
        <>
          <div className="push-device-action">
            {homeScreen ? (
              <p className="notice push-ios" role="note">
                Dodaj do ekranu początkowego, żeby dostawać powiadomienia: w Safari stuknij „Udostępnij”, potem „Do ekranu początkowego”, i otwórz aplikację z ikony.
              </p>
            ) : !supported ? (
              <p className="muted">{REASONS.unsupported}</p>
            ) : onThisDevice ? (
              <p className="push-on">
                <span className="push-dot" aria-hidden="true" /> Powiadomienia są włączone na tym urządzeniu.
              </p>
            ) : (
              <button type="button" className="btn primary" disabled={enable.isPending} onClick={() => enable.mutate()}>
                {enable.isPending ? 'Włączanie…' : 'Włącz powiadomienia na tym urządzeniu'}
              </button>
            )}
            {problem && <p className="alert" role="alert">{problem}</p>}
          </div>

          <h3 className="push-sub">Moje urządzenia</h3>
          {data.devices.length === 0 ? (
            <p className="muted">Nie masz jeszcze urządzeń z włączonymi powiadomieniami.</p>
          ) : (
            <ul className="settings-list" aria-label="Moje urządzenia">
              {data.devices.map((d) => (
                <li key={d.id} className="settings-row">
                  <span className="settings-row-main">
                    <span>
                      {d.device}
                      {d.endpoint === thisEndpoint && <span className="muted"> · to urządzenie</span>}
                    </span>
                    <span className="muted">Włączone {formatWhen(d.createdAt)}{d.lastUsedAt ? ` · ostatnie powiadomienie ${formatWhen(d.lastUsedAt)}` : ''}</span>
                  </span>
                  <button type="button" className="btn ghost small" disabled={remove.isPending} onClick={() => remove.mutate(d)} aria-label={`Usuń urządzenie ${d.device}`}>
                    Usuń
                  </button>
                </li>
              ))}
            </ul>
          )}
          {data.devices.length > 0 && (
            <p className="push-test">
              <button type="button" className="btn small" disabled={test.isPending} onClick={() => test.mutate()}>
                {test.isPending ? 'Wysyłanie…' : 'Wyślij próbne powiadomienie'}
              </button>
              {tested && <span className="muted" role="status"> {tested}</span>}
            </p>
          )}

          <h3 className="push-sub">Co wysyłać na telefon i komputer</h3>
          <ul className="push-types" aria-label="Rodzaje powiadomień na telefon i komputer">
            {data.types.map((t) => (
              <li key={t.id}>
                <label className="push-check">
                  <input type="checkbox" checked={ticked[t.id] ?? t.enabled} onChange={(e) => toggle.mutate({ id: t.id, enabled: e.target.checked })} />
                  {t.label}
                </label>
              </li>
            ))}
          </ul>
          <p className="hint">Pozostałe powiadomienia są tylko w dzwonku. Na ekranie blokady nie pokazujemy kwot ani numerów kont.</p>
        </>
      )}
      {error && <p className="alert" role="alert">{errorText(error, 'Nie udało się zapisać')}</p>}
    </section>
  );
}
